import { Capacitor } from "@capacitor/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App as AntdApp, ConfigProvider } from "antd";
import ruRU from "antd/lib/locale/ru_RU";
import { PushEventsHandler } from "features/push-notifications";
import { useEffect } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import { BrowserRouter } from "react-router-dom";
import SmartBanner from "react-smartbanner";
import "react-smartbanner/dist/main.css";
import { brand } from "shared/config";
import i18n from "shared/i18n/i18n";
import { AntdAppInitializer } from "shared/lib/antd-global";
import { ErrorBoundary } from "shared/ui/error-boundary";
import { DeepLinkHandler } from "./DeepLinkHandler";
import { AppRoutes } from "./router/app-routes";

const LanguageInitializer = ({ children }: any) => {
  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");
    if (
      savedLanguage &&
      (savedLanguage === "ru" ||
        savedLanguage === "kz" ||
        savedLanguage === "en")
    ) {
      i18n.changeLanguage(savedLanguage);
    }
  }, []);

  return children;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Отключаем повторные запросы при монтировании компонента
      refetchOnMount: false,
      // Отключаем повторные запросы при восстановлении окна/вкладки
      refetchOnWindowFocus: false,
      // Отключаем повторные запросы при переподключении к сети
      refetchOnReconnect: false,
      // Данные считаются свежими в течение 1 минут (вместо стандартных 0)
      staleTime: 60 * 1000,
      // Кэш хранится 10 минут после того, как на данные нет подписок
      gcTime: 10 * 60 * 1000,
      // Количество повторных попыток при ошибке
      retry: 1,
      // Задержка между повторными попытками
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

const App = () => {
  const { t } = useTranslation();
  const isWeb = Capacitor.getPlatform() === "web";

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <LanguageInitializer>
            <ConfigProvider
              locale={ruRU}
              theme={{
                token: {
                  colorPrimary: brand.colorPrimary,
                  colorLink: brand.colorPrimary,
                  fontFamily:
                    brand.fontFamily ??
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
                },
              }}
            >
              <AntdApp>
                <BrowserRouter>
                  <AntdAppInitializer />
                  <DeepLinkHandler />
                  <PushEventsHandler />
                  {isWeb && (
                    <SmartBanner
                      title={brand.appName}
                      price={{
                        ios: t("smart_banner.free"),
                        android: t("smart_banner.free"),
                      }}
                      button={t("smart_banner.install")}
                      position="top"
                    />
                  )}
                  <AppRoutes />
                </BrowserRouter>
              </AntdApp>
            </ConfigProvider>
          </LanguageInitializer>
        </I18nextProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};
export default App;
